import { WEEKDAYS, WEEKDAY_LABELS, type Weekday } from '../lib/schedule';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description?: string;
}

/** A set of large, tappable radio cards. */
export function ChoiceGroup<T extends string>({
  name,
  value,
  onChange,
  options,
  columns = 1
}: {
  name: string;
  value: T | null;
  onChange: (value: T) => void;
  options: ChoiceOption<T>[];
  columns?: 1 | 2 | 3;
}) {
  const grid = columns === 3 ? 'sm:grid-cols-3' : columns === 2 ? 'sm:grid-cols-2' : '';
  return (
    <div role="radiogroup" className={`grid gap-2 ${grid}`}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <label
            key={option.value}
            className={`flex min-h-12 cursor-pointer items-start gap-3 rounded-lg border p-3 text-sm transition ${
              selected ? 'border-clinical-primary bg-clinical-primary/10' : 'border-clinical-line bg-clinical-card hover:bg-clinical-surface'
            }`}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={selected}
              onChange={() => onChange(option.value)}
              className="mt-0.5 h-4 w-4 accent-[rgb(var(--rf-primary))]"
            />
            <span>
              <span className="block font-medium">{option.label}</span>
              {option.description ? <span className="mt-0.5 block text-xs text-clinical-muted">{option.description}</span> : null}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/** Toggle buttons for the days of the week. */
export function DayPicker({ value, onChange, label }: { value: Weekday[]; onChange: (days: Weekday[]) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="grid grid-cols-7 gap-1.5">
      {WEEKDAYS.map((day) => {
        const on = value.includes(day);
        return (
          <button
            key={day}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? value.filter((selected) => selected !== day) : [...value, day])}
            className={`focus-ring min-h-11 rounded-lg border text-xs font-semibold transition ${
              on ? 'border-clinical-primary bg-clinical-primary text-white' : 'border-clinical-line bg-clinical-card text-clinical-muted'
            }`}
          >
            {WEEKDAY_LABELS[day]}
          </button>
        );
      })}
    </div>
  );
}
